import useSWR from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

// const fetcher = async ([url, payload]) => {
//   const response = await fleetApi.post(url + getTzParam(), payload);
//   return response;
// };

export const useTireInventoryList = (page, rowsPerPage, selectedCompanies = [], searchKey, searchValue) => {
  const isFetchAll = page === undefined;
  const start = isFetchAll ? 0 : page * rowsPerPage;
  const companies = (selectedCompanies || []).map((c) => c.value).join(',');

  const payload = useMemo(() => {
    if (isFetchAll) return null;
    const fb = new FilterBuilder().setPaging(start, rowsPerPage);

    if (searchKey && searchValue) {
      if (searchKey === 'tire_size_code_internal') {
        fb.like('tire_size_code_internal', searchValue, 'OR');
        fb.like('tire_size_code', searchValue, 'OR');
      } else if (searchKey !== 'all') {
        fb.like(searchKey, searchValue, 'AND');
      } else {
        fb.like('batch_number', searchValue, 'OR');
        fb.like('tire_brand_name', searchValue, 'OR');
        fb.like('tire_size_code_internal', searchValue, 'OR');
        fb.like('tire_size_code', searchValue, 'OR');
        fb.like('tire_spec_name', searchValue, 'OR');
        fb.like('warehouse_name', searchValue, 'OR');
        fb.like('supplier', searchValue, 'OR');
      }
    }

    fb.setOrder(['created_at'], false);

    if (companies) {
      if (companies.includes(',')) {
        fb.in('company_id', companies);
      } else {
        fb.equal('company_id', companies);
      }
    } else {
      fb.filterByCompany();
    }

    return fb.build();
  }, [isFetchAll, start, rowsPerPage, searchKey, searchValue, companies]);

  const endpoint = `/tire-inventory/search`;

  const {
    data,
    error,
    isLoading,
    mutate: swrMutate
  } = useSWR(
    payload ? [endpoint, payload] : null,
    async ([url, body]) => {
      const response = await fleetApi.post(url + getTzParam(), body);
      return response;
    },
    {
      keepPreviousData: true
    }
  );

  return {
    data: Array.isArray(data?.data?.data || data?.data) ? data?.data?.data || data?.data : [],
    totalRecord: data?.data?.totalRecord || data?.data?.dataSize || data?.totalRecord || data?.dataSize || 0,
    isLoading,
    error,
    mutate: swrMutate,
    refetch: swrMutate
  };
};

const createDetailListHook = (endpointModifier) => {
  return (page, rowsPerPage, selectedCompanies = [], batchNumber, searchKey, searchValue) => {
    const start = page * rowsPerPage;
    const companies = (selectedCompanies || []).map((c) => c.value).join(',');

    const payload = useMemo(() => {
      if (!batchNumber) return null; // Wait for batchNumber to be available

      const fb = new FilterBuilder()
        .setPaging(start, rowsPerPage || 100)
        .setOrder(['created_at'], false)
        .addJoin('tire_item', [
          'name',
          'tire_brand_name',
          'tire_size_code',
          'tire_spec_name',
          'status',
          'total_price',
          'receive_date',
          'supplier',
          'batch_number',
          'warehouse_id',
          'warehouse_name'
        ])
        .addJoin('company', ['name']);

      fb.joins.push({
        name: 'm_tire_item.m_tire_spec',
        column_results: ['pattern', 'original_tread_depth', 'pressure_std'],
        relation: 'tire_spec_id',
        reference: 'id'
      });

      if (companies) {
        if (companies.includes(',')) {
          fb.in('company_id', companies);
        } else {
          fb.equal('company_id', companies);
        }
      }

      fb.equal('batch_number', batchNumber, 'AND', 'm_tire_item');

      if (searchKey && searchValue && searchKey !== 'all') {
        fb.like(searchKey, searchValue, 'AND');
      }

      return fb.build();
    }, [start, rowsPerPage, companies, batchNumber, searchKey, searchValue]);

    const endpoint = `/tire-batch/search?${endpointModifier}`;

    const {
      data,
      error,
      isLoading,
      mutate: swrMutate
    } = useSWR(
      payload ? [endpoint, payload] : null,
      async ([url, body]) => {
        const response = await fleetApi.post(url + getTzParam().replace('?', '&'), body);
        return response;
      },
      {
        keepPreviousData: true
      }
    );

    return {
      data: Array.isArray(data?.data?.data || data?.data) ? data?.data?.data || data?.data : [],
      totalRecord: data?.data?.totalRecord || data?.data?.dataSize || data?.totalRecord || data?.dataSize || 0,
      isLoading,
      error,
      mutate: swrMutate,
      refetch: swrMutate
    };
  };
};

export const useDetailTireInventoryList = createDetailListHook('action=remove&is_need_action=false');
export const useDetailTireFitList = createDetailListHook('action=fit');
export const useDetailTireRepairScrapList = createDetailListHook('action=remove&is_need_action=true');
